if (!customElements.get('tour-dates')) {
  customElements.define(
    'tour-dates',
    class TourDates extends HTMLElement {
      connectedCallback() {
        if (this.loaded) return;
        this.loaded = true;

        this.body = this.querySelector('.tour-dates__body');
        this.template = this.querySelector('.tour-dates__row-template');
        this.footer = this.querySelector('.tour-dates__footer');
        this.initialCount = parseInt(this.dataset.initialCount, 10) || 5;

        this.querySelector('.tour-dates__show-all').addEventListener('click', this.showAll.bind(this));
        this.load();
      }

      async load() {
        try {
          const response = await fetch(this.buildUrl());
          if (!response.ok) return;
          const events = await response.json();
          // The API returns an error object (not an array) for unknown artists.
          if (!Array.isArray(events)) return;

          const upcoming = events
            .filter((event) => this.isUpcoming(event.datetime))
            .sort((a, b) => a.datetime.localeCompare(b.datetime));
          if (upcoming.length === 0) return;

          this.render(upcoming);
          this.hidden = false;
        } catch (error) {
          console.error('Tour dates could not be loaded', error);
        }
      }

      buildUrl() {
        // Bandsintown requires these characters to be double-encoded in artist names.
        const artist = encodeURIComponent(this.dataset.artist)
          .replace(/%2F/g, '%252F')
          .replace(/%3F/g, '%253F')
          .replace(/\*/g, '%252A')
          .replace(/%22/g, '%27C');
        const appId = encodeURIComponent(this.dataset.appId);
        return `https://rest.bandsintown.com/artists/${artist}/events/?app_id=${appId}&date=upcoming`;
      }

      // Event datetimes are in the venue's local time with no offset, so compare by calendar date only.
      isUpcoming(datetime) {
        if (!datetime) return false;
        const now = new Date();
        const today = [now.getFullYear(), now.getMonth() + 1, now.getDate()]
          .map((part) => String(part).padStart(2, '0'))
          .join('-');
        return datetime.slice(0, 10) >= today;
      }

      formatDate(datetime) {
        const [year, month, day] = datetime.slice(0, 10).split('-').map(Number);
        const date = new Date(Date.UTC(year, month - 1, day));
        const weekday = date.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
        const monthName = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
        return `${weekday}, ${monthName} ${day}`.toUpperCase();
      }

      formatLocation(venue = {}) {
        if (venue.location) return venue.location;
        const region = venue.country === 'United States' ? venue.region : venue.country;
        return [venue.city, region].filter(Boolean).join(', ');
      }

      render(events) {
        const artist = this.dataset.artist.toLowerCase();

        events.forEach((event, index) => {
          const row = this.template.content.firstElementChild.cloneNode(true);
          const venue = event.venue || {};
          const offer = (event.offers || []).find((offer) => offer.type === 'Tickets' && offer.url);
          const supporting = (event.lineup || []).filter((name) => name.toLowerCase() !== artist);

          row.querySelector('.tour-dates__date').textContent = this.formatDate(event.datetime);

          const lineup = row.querySelector('.tour-dates__lineup');
          if (supporting.length > 0) {
            lineup.textContent = `with ${supporting.join(', ')}`;
          } else {
            lineup.remove();
          }

          const venueLink = row.querySelector('.tour-dates__venue');
          venueLink.textContent = venue.name || event.title || '';
          venueLink.href = event.url;

          row.querySelector('.tour-dates__cell--location').textContent = this.formatLocation(venue);

          const tickets = row.querySelector('.tour-dates__tickets');
          tickets.href = offer ? offer.url : event.url;
          tickets.setAttribute('aria-label', `Tickets for ${venueLink.textContent} on ${this.formatDate(event.datetime)}`);

          if (index >= this.initialCount) row.hidden = true;
          this.body.appendChild(row);
        });

        this.footer.hidden = events.length <= this.initialCount;
      }

      showAll() {
        this.body.querySelectorAll('.tour-dates__row[hidden]').forEach((row) => (row.hidden = false));
        this.footer.hidden = true;
      }
    }
  );
}
